import { PopoverPosition as BlueprintPopoverPosition } from '@blueprintjs/core';
import { Popover2 } from '@blueprintjs/popover2';
import {
  OverflowMenuHorizontal,
  Settings,
  TrashCan,
} from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import {
  MenuItem,
  MenuItems,
  OutlinedIconButton,
} from 'vet-bones/design-system/components';

import { withConfirmation } from 'src/components/withConfirmation';
import { TEST_IDS } from 'src/constants/testIds';
import { useRemoveRelationshipMapping } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveRelationshipMapping';
import { useRemoveResourceMapping } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveResourceMapping';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { RelationshipMapping } from 'src/containers/ProjectDiagram/types/data';
import {
  isMapping,
  isRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/data';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { stopPropagation } from 'src/utils/handlers/stopPropagation';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';

import styles from 'src/containers/ProjectSidebar/projectSidebar.module.scss';

type ProjectSidebarMappingMenuProps = {
  mappingId: string | undefined;
};

export const ProjectSidebarMappingMenu: React.VFC<
  ProjectSidebarMappingMenuProps
> = ({ mappingId }) => {
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();
  const model = diagram?.model;
  const removeMapping = useRemoveResourceMapping();
  const removeRelationshipMapping = useRemoveRelationshipMapping();

  const handleOpenSettings = React.useCallback(() => {
    if (!mappingId) {
      return;
    }
    dispatch(
      uiSlice.actions.toggledProjectMappingSettingsDrawer({
        isOpen: true,
        mappingId,
      })
    );
  }, [dispatch, mappingId]);

  const handleRemoveMapping = React.useCallback(() => {
    if (!diagram || !mappingId || !model) {
      return;
    }
    const linkDataToRemove: RelationshipMapping[] = [];
    const mapping = model.linkDataArray.find(
      (linkData) => isMapping(linkData) && linkData.value === mappingId
    );

    if (mapping) {
      removeMapping(mapping.key);
      model.linkDataArray.forEach((linkData) => {
        if (
          isRelationshipMapping(linkData) &&
          (linkData.from === mapping.value || linkData.to === mapping.value)
        ) {
          linkDataToRemove.push(linkData);
        }
      });
      linkDataToRemove.forEach((linkData) =>
        removeRelationshipMapping(linkData.key)
      );
    }
  }, [diagram, mappingId, model, removeMapping, removeRelationshipMapping]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={styles.projectSidebarMappingsMappingMenu}
      onClick={stopPropagation}
    >
      <Popover2
        content={
          <MenuItems className={styles.projectSidebarSmallMenuItems}>
            <MenuItem
              leftIcon={Settings}
              onClick={handleOpenSettings}
              testId={TEST_IDS.projectSidebar.mappingMenu.settingsButton}
              text={copy.components.project.mappings.settings}
            />
            <MenuItem
              leftIcon={TrashCan}
              onClick={() => {
                withConfirmation({
                  message: copy.components.project.mappings.deleteConfirmation,
                  onConfirmationSuccess: handleRemoveMapping,
                  noButtonOutlined: true,
                });
              }}
              testId={TEST_IDS.projectSidebar.mappingMenu.deleteButton}
              text={copy.components.project.mappings.delete}
            />
          </MenuItems>
        }
        minimal
        popoverClassName={classNames(
          'vb-menu-popover',
          `vb-popover-position--${BlueprintPopoverPosition.BOTTOM_RIGHT}`
        )}
        position={BlueprintPopoverPosition.BOTTOM_RIGHT}
      >
        <OutlinedIconButton
          elevated
          leftIcon={OverflowMenuHorizontal}
          testId={TEST_IDS.projectSidebar.mappingMenu.button}
        />
      </Popover2>
    </div>
  );
};
